import React from 'react'
import { Box, Typography, Chip, IconButton, Tooltip } from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatDateTime } from '../utils/formatters'

const ROW_STYLE = {
  gap: 8,
  padding: '10px 12px',
  background: '#f5f4f0',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: 6,
  marginBottom: 6
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']

function getFileIcon(fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase()
  return IMAGE_EXTENSIONS.includes(ext) ? 'image' : 'file'
}

// Icon button styled to look like an actual clickable button (bordered chip), not a bare icon.
function ActionButton({ icon, onClick, color, title }) {
  const button = (
    <IconButton
      size='small'
      onClick={onClick}
      style={{
        border: '1px solid rgba(0,0,0,0.28)',
        borderRadius: 6,
        padding: 6,
        background: '#fff'
      }}
    >
      <FontAwesomeIcon icon={['fal', icon]} style={{ fontSize: 12, color: color || 'rgba(0,0,0,0.65)' }} />
    </IconButton>
  )
  return title ? <Tooltip title={title}><span>{button}</span></Tooltip> : button
}

function downloadLocalFile(file) {
  const link = document.createElement('a')
  link.href = URL.createObjectURL(file)
  link.download = file.name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

// Read-only row for a server-persisted attachment (attachmentInfo[]) — locked, downloadable, never removable
// regardless of status (DRFT or SUBM), per BR-4/AC5. SUBM shows a lock icon; DRFT shows a check icon
// (already uploaded in a prior save, just not submitted to NAIC yet).
function ExistingAttachmentRow({ attachment, onDownload, onDelete, disabled }) {
  const isSubmitted = attachment.status === 'SUBM'
  return (
    <Box display='flex' alignItems='center' style={ROW_STYLE}>
      <FontAwesomeIcon icon={['fal', getFileIcon(attachment.fileName)]} style={{ fontSize: 13, opacity: 0.6 }} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography variant='caption' style={{ display: 'block', wordBreak: 'break-all' }}>{attachment.fileName}</Typography>
        <Typography variant='caption' color='textSecondary'>
          {typeof attachment.fileSizeMb === 'number' ? `${attachment.fileSizeMb} MB` : ''}
          {typeof attachment.fileSizeMb === 'number' ? ' · ' : ''}
          {`${isSubmitted ? 'Submitted' : 'Uploaded'} ${formatDateTime(attachment.updatedDate || attachment.createdDate)}`}
        </Typography>
      </Box>
      {isSubmitted ? (
        <Tooltip title='Read-only — cannot be removed'>
          <span><FontAwesomeIcon icon={['fal', 'lock']} style={{ fontSize: 13, opacity: 0.5 }} /></span>
        </Tooltip>
      ) : (
        <Tooltip title='Successfully uploaded'>
          <span><FontAwesomeIcon icon={['fal', 'circle-check']} style={{ fontSize: 14, color: '#2e7d32' }} /></span>
        </Tooltip>
      )}
      <ActionButton icon='download' title='Download' onClick={() => onDownload && onDownload(attachment)} />
      {!isSubmitted && onDelete && !disabled && (
        <ActionButton icon='xmark' title='Remove' onClick={() => onDelete(attachment)} />
      )}
    </Box>
  )
}

// Row for a locally-chosen file, not yet uploaded to the server — downloadable (local copy) and removable.
// No status icon — it isn't saved anywhere yet, so neither "locked" nor "uploaded" applies.
function PendingFileRow({ file, onDownload, onRemove, disabled }) {
  return (
    <Box display='flex' alignItems='center' style={ROW_STYLE}>
      <FontAwesomeIcon icon={['fal', getFileIcon(file.name)]} style={{ fontSize: 13, opacity: 0.6 }} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography variant='caption' style={{ display: 'block', wordBreak: 'break-all' }}>{file.name}</Typography>
        <Typography variant='caption' color='textSecondary'>
          {(file.size / 1024).toFixed(0)} KB &middot; Uploaded just now
        </Typography>
      </Box>
      <ActionButton icon='download' title='Download' onClick={() => onDownload(file)} />
      {!disabled && <ActionButton icon='xmark' title='Remove' onClick={onRemove} />}
    </Box>
  )
}

/**
 * Supporting Documents display + upload control.
 * `attachments` (attachmentInfo[] from server) are always read-only, per BR-4/AC5.
 * Only status=SUBM rows count as "Previously Submitted" — status=DRFT rows are saved but not yet
 * submitted to NAIC, so they render alongside the local pending-upload list instead.
 * Pass `editable` to also render the pending-upload section + dropzone for adding new files.
 */
function SupportingDocumentsSection({
  attachments = [],
  onDownload,
  onDeleteAttachment,
  editable = false,
  pendingFiles = [],
  onFileChange,
  onRemovePending,
  fileInputRef,
  uploadErrors = [],
  onClearErrors,
  required = false,
  disabled = false
}) {
  const submittedAttachments = attachments.filter((att) => att.status === 'SUBM')
  const draftAttachments = attachments.filter((att) => att.status !== 'SUBM')
  const draftHeading = editable ? 'Add Additional Documents' : 'Pending Submission'

  return (
    <Box>
      <Box display='flex' alignItems='center' mb={1} style={{ gap: 8 }}>
        <FontAwesomeIcon icon={['fal', 'paperclip']} style={{ fontSize: 14 }} />
        <Typography variant='subtitle2'>Supporting Documents</Typography>
        {editable && (
          <Chip
            label={required ? 'Required' : 'Optional'}
            size='small'
            style={{
              fontSize: 11,
              height: 20,
              color: required ? '#fff' : undefined,
              background: required ? '#d32f2f' : undefined
            }}
          />
        )}
      </Box>

      {submittedAttachments.length > 0 && (
        <Box mb={2}>
          <Typography variant='caption' style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Previously Submitted
          </Typography>
          {submittedAttachments.map((att) => (
            <ExistingAttachmentRow key={att.id} attachment={att} onDownload={onDownload} />
          ))}
        </Box>
      )}

      {!editable && draftAttachments.length > 0 && (
        <Box mb={2}>
          <Typography variant='caption' style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            {draftHeading}
          </Typography>
          {draftAttachments.map((att) => (
            <ExistingAttachmentRow key={att.id} attachment={att} onDownload={onDownload} />
          ))}
        </Box>
      )}

      {attachments.length === 0 && !editable && (
        <Typography variant='caption' color='textSecondary'>No supporting documents.</Typography>
      )}

      {editable && (
        <>
          {(draftAttachments.length > 0 || pendingFiles.length > 0) && (
            <Box mb={2}>
              <Typography variant='caption' style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                {draftHeading}
              </Typography>
              {draftAttachments.map((att) => (
                <ExistingAttachmentRow key={att.id} attachment={att} onDownload={onDownload} onDelete={onDeleteAttachment} disabled={disabled} />
              ))}
              {pendingFiles.map((file, idx) => (
                <PendingFileRow
                  key={idx}
                  file={file}
                  disabled={disabled}
                  onDownload={downloadLocalFile}
                  onRemove={() => onRemovePending(idx)}
                />
              ))}
            </Box>
          )}

          {uploadErrors.length > 0 && (
            <Box mb={1} style={{ background: '#fff3f3', border: '1px solid #f44336', borderRadius: 6, padding: '8px 12px' }}>
              {uploadErrors.map((err, i) => (
                <Typography key={i} variant='caption' style={{ color: '#d32f2f', display: 'block' }}>
                  {err}
                </Typography>
              ))}
              <Typography
                variant='caption'
                style={{ color: '#1976d2', cursor: 'pointer', display: 'block', marginTop: 4 }}
                onClick={onClearErrors}
              >
                Dismiss
              </Typography>
            </Box>
          )}

          {disabled ? (
            <Box
              display='flex'
              alignItems='center'
              style={{ gap: 8, padding: '10px 12px', background: '#fafafa', border: '1px dashed rgba(0,0,0,0.18)', borderRadius: 8 }}
            >
              <FontAwesomeIcon icon={['fal', 'lock']} style={{ fontSize: 14, opacity: 0.5 }} />
              <Typography variant='caption' color='textSecondary'>
                Upload disabled — this filing has a connectivity issue. Documents remain viewable and downloadable.
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
                style={{
                  minHeight: 90,
                  border: `2px dashed ${required ? '#f44336' : 'rgba(0,0,0,0.18)'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: '#fafafa',
                  padding: '16px 12px',
                  gap: 6
                }}
              >
                <FontAwesomeIcon icon={['fal', 'file-arrow-up']} style={{ fontSize: 22, opacity: 0.55 }} />
                <Typography variant='caption' color='textSecondary'>
                  Click to upload or drag &amp; drop
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  PDF, JPG, PNG &middot; Max 10 MB per file &middot; Up to 5 files
                </Typography>
              </Box>
              <input
                ref={fileInputRef}
                type='file'
                multiple
                accept='.pdf,.jpg,.jpeg,.png'
                style={{ display: 'none' }}
                onChange={onFileChange}
              />
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default SupportingDocumentsSection
